import { TypedDocumentNode } from '@apollo/client';
import { useMutation, useQuery } from '@apollo/client/react';
import { Mutation, MutationDeleteQrCodeVisitArgs, QrCodeVisitWeb, Query } from 'graphql/generated/graphqlTypes';
import { DELETE_QR_CODE_VISIT } from 'graphql/v2/mutations';
import { GET_QR_CODE_VISITS_FOR_TENANT } from 'graphql/v2/queries';
import { useRegisterBackgroundRefetch } from 'hooks/useBackgroundRefetch/useBackgroundRefetch';
import { useEffect, useRef } from 'react';
import { TranslatedHookProps } from 'v2/hooks/hooks.types';

// Typing the documents (instead of passing generics to the Apollo hooks) avoids the deprecated hook signatures.
const GET_QR_CODE_VISITS_FOR_TENANT_TYPED: TypedDocumentNode<Pick<Query, 'getQrCodeVisitsForTenant'>> =
  GET_QR_CODE_VISITS_FOR_TENANT;
const DELETE_QR_CODE_VISIT_TYPED: TypedDocumentNode<
  Pick<Mutation, 'deleteQrCodeVisit'>,
  MutationDeleteQrCodeVisitArgs
> = DELETE_QR_CODE_VISIT;

const useQrCodeVisits = ({ handleFetchError, translation }: TranslatedHookProps) => {
  const { data, loading, error } = useQuery(GET_QR_CODE_VISITS_FOR_TENANT_TYPED, {
    notifyOnNetworkStatusChange: true
  });

  // Keep the latest handler and translation in a ref so changes to their
  // references don't trigger duplicate error notifications.
  const latestErrorReporter = useRef({ handleFetchError, translation });

  useEffect(() => {
    latestErrorReporter.current = { handleFetchError, translation };
  });

  useEffect(() => {
    if (error) {
      latestErrorReporter.current.handleFetchError(latestErrorReporter.current.translation.visits_error_fetching);
    }
  }, [error]);

  // `loading` is used instead of the network status because a failed refetch leaves the status on `error`, which would
  // keep the "refreshing" indicator on until the next success.
  const isRefetchingQrCodeVisits = !!data && loading;

  useRegisterBackgroundRefetch(isRefetchingQrCodeVisits);

  const qrCodeVisits: QrCodeVisitWeb[] = data?.getQrCodeVisitsForTenant ?? [];

  const [cancelQrCodeVisitMutation] = useMutation(DELETE_QR_CODE_VISIT_TYPED, {
    refetchQueries: [GET_QR_CODE_VISITS_FOR_TENANT],
    awaitRefetchQueries: true
  });

  const cancelQrCodeVisit = async (qrCodeVisitId: string) => {
    await cancelQrCodeVisitMutation({ variables: { qrCodeVisitId } });
  };

  return { qrCodeVisits, loading: loading && !data, isRefetchingQrCodeVisits, cancelQrCodeVisit };
};

export default useQrCodeVisits;
